/**
 * Deterministic primary-key formulas for the notification collections.
 *
 * Kept in a leaf module (no React, no provider imports) so replication
 * mappers can rewrite incoming server ids to the canonical local form
 * without participating in the `notifications-provider` ↔ `rxdb-provider`
 * import cycle.
 *
 * `dismissalId` uses a length-prefixed encoding rather than naive
 * concatenation so producer-controlled `key` and `version` strings cannot
 * collide across different `(userId, key, version)` tuples — `version=null`
 * vs `version="*"`, embedded `-`, etc. are all unambiguous.
 */

/** Local-only `notification` row id: `${userId}|${key}`. */
export const notificationId = (userId: string, key: string) =>
  `${userId}|${key}`;

/** `notification-bell-state` row id: one row per user. */
export const bellStateId = (userId: string) => `bellstate-${userId}`;

const encodeComponent = (s: string) => `${s.length}-${s}`;

/**
 * Length-prefix encoding of `(userId, key, version)` for the dismissal row's
 * primary key. The leading `dismissal-` keeps it human-readable in the rxdb
 * inspector; `0`/`1` after the userId/key segments encodes whether the
 * producer supplied a version. Collision-safe for any opaque producer
 * strings.
 */
export const dismissalId = (
  userId: string,
  key: string,
  version: string | null,
) =>
  `dismissal-${encodeComponent(userId)}-${encodeComponent(key)}-${
    version === null ? "0" : `1-${encodeComponent(version)}`
  }`;
